import { Alert, Box, Button, Paper, Stack, TextField, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNotify, useTranslate } from "react-admin";

import {
  beginAdminMfaEnrollment,
  cancelAdminMfaEnrollment,
  completeAdminMfa,
  disableAdminMfa,
  getAdminMfaSettings,
  replaceAdminMfaSession,
} from "../auth/authProvider";
import { AdminMfaResponse, AdminMfaStatus } from "../generated/graphql";
import { getAuthData } from "../lib/authStorage";
import { borderRadius, color, shadows, space } from "../theme/designTokens";
import { AdminMfaForm } from "./AdminMfaForm";

type Mode = "off" | "setup" | "codes" | "disable";
type EnrollmentResponse = AdminMfaResponse & { accountEmail: string };

const isRetryable = (response: AdminMfaResponse): boolean =>
  response.status === AdminMfaStatus.Expired || response.status === AdminMfaStatus.Locked;

const normalizeEmail = (email: string): string => email.trim().toLocaleLowerCase();

const isCurrentAdminAccount = (expectedEmail: string): boolean => {
  const auth = getAuthData();
  return auth?.surface === "admin" && normalizeEmail(auth.email) === expectedEmail;
};

export const AdminMfaSettingsSection = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [mode, setMode] = useState<Mode>("off");
  const [password, setPassword] = useState("");
  const [response, setResponse] = useState<EnrollmentResponse | null>(null);
  const [codes, setCodes] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resetCodeVersion, setResetCodeVersion] = useState(0);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const settings = await getAdminMfaSettings();
      setEnabled(settings.enabled);
    } catch {
      setError(translate("mfa.settingsError"));
    } finally {
      setLoading(false);
    }
  }, [translate]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const clearTransient = (): void => {
    setPassword("");
    setResponse(null);
    setCodes([]);
    setError(null);
    setResetCodeVersion(value => value + 1);
  };

  const begin = async (): Promise<void> => {
    const auth = getAuthData();
    if (!auth || auth.surface !== "admin") {
      setError(translate("mfa.settingsError"));
      return;
    }
    const accountEmail = normalizeEmail(auth.email);
    setLoading(true);
    setError(null);
    try {
      const next = await beginAdminMfaEnrollment(password);
      if (!isCurrentAdminAccount(accountEmail)) {
        clearTransient();
        setError(translate("mfa.settingsError"));
        return;
      }
      if (next.status === AdminMfaStatus.Locked) {
        setPassword("");
        setResponse(null);
        setError(
          next.retryAfterSeconds
            ? translate("mfa.lockedWithRetry", { seconds: next.retryAfterSeconds })
            : translate("mfa.locked")
        );
        return;
      }
      if (next.status !== AdminMfaStatus.EnrollmentRequired || !next.challengeToken) {
        throw new Error("Unexpected MFA response");
      }
      setPassword("");
      setResponse({ ...next, accountEmail });
    } catch {
      setError(translate("mfa.settingsError"));
    } finally {
      setLoading(false);
    }
  };

  const cancelSetup = async (): Promise<void> => {
    if (!response) {
      clearTransient();
      setMode("off");
      return;
    }
    if (!isCurrentAdminAccount(response.accountEmail)) {
      clearTransient();
      setError(translate("mfa.settingsError"));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const cancelled = await cancelAdminMfaEnrollment();
      if (!cancelled) {
        clearTransient();
        setEnabled(null);
        setMode("off");
        await refresh();
        return;
      }
      clearTransient();
      setEnabled(false);
      setMode("off");
    } catch {
      setError(translate("mfa.settingsError"));
    } finally {
      setLoading(false);
    }
  };

  const completeSetup = async (code: string, recovery: boolean): Promise<void> => {
    const challengeToken = response?.challengeToken;
    const accountEmail = response?.accountEmail;
    if (!challengeToken || !accountEmail) return;
    if (!isCurrentAdminAccount(accountEmail)) {
      clearTransient();
      setError(translate("mfa.settingsError"));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await completeAdminMfa({ challengeToken, code, recovery });
      if (!isCurrentAdminAccount(accountEmail)) {
        clearTransient();
        setError(translate("mfa.settingsError"));
        return;
      }
      if (result.status === AdminMfaStatus.InvalidCode) {
        setError(translate("mfa.invalidCode"));
        setResetCodeVersion(value => value + 1);
        return;
      }
      if (isRetryable(result)) {
        setResponse(null);
        setError(
          result.status === AdminMfaStatus.Locked && result.retryAfterSeconds
            ? translate("mfa.lockedWithRetry", { seconds: result.retryAfterSeconds })
            : translate(result.status === AdminMfaStatus.Locked ? "mfa.locked" : "mfa.expired")
        );
        return;
      }
      if (result.status !== AdminMfaStatus.Authenticated || !result.accessToken) {
        throw new Error("Unexpected MFA response");
      }
      replaceAdminMfaSession(result.accessToken, accountEmail);
      setEnabled(true);
      setResponse(null);
      setPassword("");
      setCodes(result.recoveryCodes ?? []);
      setMode("codes");
    } catch {
      setError(translate("mfa.settingsError"));
    } finally {
      setLoading(false);
    }
  };

  const submitDisable = async (code: string, recovery: boolean): Promise<void> => {
    if (!password) {
      setError(translate("mfa.currentPasswordRequired"));
      return;
    }
    const expectedEmail = getAuthData()?.email;
    if (!expectedEmail) return;
    setLoading(true);
    setError(null);
    try {
      const result = await disableAdminMfa({ currentPassword: password, code, recovery });
      if (result.status === AdminMfaStatus.InvalidCode || result.status === AdminMfaStatus.Locked) {
        setError(
          result.status === AdminMfaStatus.Locked && result.retryAfterSeconds
            ? translate("mfa.lockedWithRetry", { seconds: result.retryAfterSeconds })
            : translate(result.status === AdminMfaStatus.Locked ? "mfa.locked" : "mfa.invalidCode")
        );
        setResetCodeVersion(value => value + 1);
        return;
      }
      if (result.status !== AdminMfaStatus.Disabled || !result.accessToken) {
        throw new Error("Unexpected MFA response");
      }
      replaceAdminMfaSession(result.accessToken, expectedEmail);
      clearTransient();
      setEnabled(false);
      setMode("off");
      notify("mfa.disabled", { type: "success" });
    } catch {
      setError(translate("mfa.settingsError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" mb={space.xs}>
        {translate("mfa.settingsTitle")}
      </Typography>
      <Paper
        variant="outlined"
        sx={{ borderColor: color.panelBorder, borderRadius: borderRadius.panel, boxShadow: shadows.card, p: space.sm2 }}
      >
        {enabled === null && !error && <Typography>{translate("mfa.loading")}</Typography>}
        {error && mode === "off" && (
          <Alert
            severity="error"
            action={
              <Button disabled={loading} onClick={() => void refresh()}>
                {translate("mfa.retry")}
              </Button>
            }
          >
            {error}
          </Alert>
        )}
        {enabled === false && mode === "off" && (
          <Stack gap={space.sm}>
            <Typography>{translate("mfa.disabledDescription")}</Typography>
            <Button variant="contained" disabled={loading} onClick={() => setMode("setup")}>
              {translate("mfa.enable")}
            </Button>
          </Stack>
        )}
        {enabled === true && mode === "off" && (
          <Stack gap={space.sm}>
            <Typography>{translate("mfa.enabledDescription")}</Typography>
            <Button variant="outlined" disabled={loading} onClick={() => setMode("disable")}>
              {translate("mfa.disable")}
            </Button>
          </Stack>
        )}
        {mode === "setup" && !response && (
          <Stack gap={space.sm}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label={translate("mfa.currentPassword")}
              name="currentPassword"
              type="password"
              value={password}
              onChange={event => setPassword(event.target.value)}
              autoComplete="current-password"
              disabled={loading}
              required
            />
            <Button variant="contained" disabled={loading || password.length === 0} onClick={() => void begin()}>
              {translate("mfa.enable")}
            </Button>
            <Button disabled={loading} onClick={cancelSetup}>
              {translate("mfa.back")}
            </Button>
          </Stack>
        )}
        {mode === "setup" && response && (
          <AdminMfaForm
            response={response}
            loading={loading}
            error={error}
            isReplacement={false}
            resetCodeVersion={resetCodeVersion}
            onSubmit={(code, recovery) => void completeSetup(code, recovery)}
            onBack={() => void cancelSetup()}
          />
        )}
        {mode === "codes" && (
          <Stack gap={space.sm}>
            <Typography variant="h6">{translate("mfa.recoveryCodesTitle")}</Typography>
            <Alert severity="warning">{translate("mfa.recoveryCodesWarning")}</Alert>
            <Box component="ul" data-testid="admin-mfa-recovery-codes">
              {codes.map(code => (
                <li key={code}>{code}</li>
              ))}
            </Box>
            <Button
              variant="contained"
              onClick={() => {
                clearTransient();
                setMode("off");
              }}
            >
              {translate("mfa.codesSaved")}
            </Button>
          </Stack>
        )}
        {mode === "disable" && (
          <Stack gap={space.sm}>
            <TextField
              label={translate("mfa.currentPassword")}
              name="currentPassword"
              type="password"
              value={password}
              onChange={event => setPassword(event.target.value)}
              autoComplete="current-password"
              disabled={loading}
              required
            />
            <AdminMfaForm
              response={{ status: AdminMfaStatus.VerificationRequired, challengeToken: "disable" }}
              loading={loading}
              error={error}
              isReplacement={false}
              resetCodeVersion={resetCodeVersion}
              onSubmit={(code, recovery) => void submitDisable(code, recovery)}
              onBack={() => {
                clearTransient();
                setMode("off");
              }}
            />
          </Stack>
        )}
      </Paper>
    </Box>
  );
};
